/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useId, useMemo, useState } from "react";

interface SparklineProps {
  values: number[];
  color: string;
  height?: number;
  /** Shown when there are fewer than two samples. */
  emptyLabel?: string;
  /** A second line drawn on the same scale (e.g. upload under download). */
  overlay?: { values: number[]; color: string };
  /** Formats the value shown under the cursor; enables the hover readout. */
  format?: (v: number) => string;
  /** Moving-average window, to calm noisy sources. */
  smooth?: number;
}

function movingAverage(vs: number[], n: number): number[] {
  if (n <= 1) return vs;
  return vs.map((_, i) => {
    const slice = vs.slice(Math.max(0, i - n + 1), i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

/** Axis-less area sparkline for compact widgets, with an optional hover value. */
export const Sparkline: React.FC<SparklineProps> = ({
  values: raw,
  color,
  height = 44,
  emptyLabel = "collecting…",
  overlay,
  format,
  smooth = 1,
}) => {
  const gid = useId();
  const [hover, setHover] = useState<number | null>(null);
  const values = useMemo(() => movingAverage(raw, smooth), [raw, smooth]);
  const second = useMemo(() => (overlay ? movingAverage(overlay.values, smooth) : undefined), [overlay, smooth]);

  if (values.length < 2) {
    return (
      <div className="flex items-center justify-center text-[12px] text-[var(--gh-mute)]" style={{ height }}>
        {emptyLabel}
      </div>
    );
  }
  const W = 100;
  const all = second ? [...values, ...second] : values;
  const max = Math.max(...all);
  const min = Math.min(...all, 0);
  const span = max - min || 1;
  const yOf = (v: number) => height - 3 - ((v - min) / span) * (height - 8);
  const trace = (vs: number[]) =>
    vs
      .map((v, i) => `${i ? "L" : "M"}${((i / Math.max(1, vs.length - 1)) * W).toFixed(2)} ${yOf(v).toFixed(2)}`)
      .join(" ");
  const line = trace(values);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setHover(Math.round(t * (values.length - 1)));
  };
  const hx = hover === null ? 0 : (hover / (values.length - 1)) * 100;

  return (
    <div
      className="relative"
      onMouseMove={format ? onMove : undefined}
      onMouseLeave={format ? () => setHover(null) : undefined}
    >
      <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" width="100%" height={height} aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0.35" />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${line} L${W} ${height} L0 ${height} Z`} fill={`url(#${gid})`} />
        <path d={line} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        {overlay && second && second.length > 1 && (
          <path d={trace(second)} fill="none" stroke={overlay.color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        )}
        {hover !== null && <line x1={hx} x2={hx} y1="0" y2={height} stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
      </svg>
      {hover !== null && format && (
        <span
          className="pointer-events-none absolute -top-1 rounded-md border border-[var(--gh-border)] bg-[var(--gh-panel)] px-1.5 py-0.5 text-[11px] tabular-nums text-[var(--gh-text)] shadow-sm"
          style={{ left: `clamp(0px, calc(${hx}% - 28px), calc(100% - 64px))`, transform: "translateY(-100%)" }}
        >
          {format(raw[hover])}
        </span>
      )}
    </div>
  );
};
